import dayjs from "dayjs";
import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { findSaleHistory, findSales } from "../services/sales";
import { toDateOnlyString } from "../utils";
import { showErrorNotification } from "./notifications";

const MANDATORY_FILTER_IDS = ["flightDates", "pax", "pnr", "saleDate", "tkt"];

export const STANDALONE_FILTER_IDS = ["pnr", "tkt"];

const initialState = {
  filters: {
    applied: [],
    byId: {
      airline: {
        id: "airline",
        placeholder: "Airline",
      },
      customer: {
        id: "customer",
        placeholder: "Customer Name",
      },
      flightDates: { id: "flightDates", placeholder: "Flight Dates" },
      pax: { id: "pax", placeholder: "Passenger Name" },
      paymentMethod: { id: "paymentMethod", placeholder: "Payment Method" },
      pnr: { id: "pnr", placeholder: "PNR" },
      sa: { id: "sa", placeholder: "Sales Agent" },
      saleDate: {
        id: "saleDate",
        placeholder: "Sale Date",
      },
      src: { id: "src", placeholder: "Source" },
      sup: { id: "sup", placeholder: "Supplier" },
      tkt: { id: "tkt", placeholder: "Ticket #" },
      type: { id: "type", placeholder: "Sale Type" },
    },
  },
  isInProgress: false,
  __next: "",
};

const searchSlice = createSlice({
  name: "search",
  initialState,
  reducers: {
    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.__next = "";
    },
    setFilter: (state, { payload: { id, value } }) => {
      const filter = state.filters.byId[id];
      if (!filter || !value) {
        return state;
      }
      filter.value = value;
      state.filters.applied = [...new Set([...state.filters.applied, id])];
    },
    unsetFilter: (state, { payload: id }) => {
      const filter = state.filters.byId[id];
      if (!filter) {
        return state;
      }
      filter.value = "";
      const filterIndex = state.filters.applied.indexOf(id);
      if (filterIndex !== -1) {
        state.filters.applied.splice(filterIndex, 1);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadSearchResults.fulfilled, (state, { payload }) => {
        state.isInProgress = false;
        state.__next = payload?.meta?.__next || "";
      })
      .addCase(loadSearchResults.pending, (state) => {
        state.isInProgress = true;
      })
      .addCase(loadSearchResults.rejected, (state) => {
        state.isInProgress = false;
      });
  },
});

export default searchSlice.reducer;

export const { resetFilters, setFilter, unsetFilter } = searchSlice.actions;

const opTransformers = {
  pax: (value) => ({
    "pax[gte]": value,
    "pax[lt]": `${value}\xff`,
  }),
  flightDates: ({ start, end }) =>
    start === end
      ? { flightDates: start }
      : {
          "flightDates[gte]": start,
          "flightDates[lte]": end,
        },
  saleDate: ({ start, end }) =>
    start === end
      ? { saleDate: start }
      : {
          "saleDate[gte]": start,
          "saleDate[lte]": end,
        },
};

export const filtersToParams = (filters) =>
  filters.reduce(
    (acc, { id, value }) => ({
      ...acc,
      ...(opTransformers[id]
        ? opTransformers[id](value)
        : value === "__is_null"
          ? { [`${id}[is]`]: "" }
          : value === "__not_null"
            ? { [`${id}[not]`]: "" }
            : { [id]: value }),
    }),
    {},
  );

export const loadDefaultSearchResults = createAsyncThunk(
  "search/loadDefaultResults",
  (_, { dispatch }) => {
    const today = toDateOnlyString(dayjs());
    dispatch(resetFilters());
    dispatch(
      applyFilter({
        id: "saleDate",
        value: { start: today, end: today },
      }),
    );
  },
);

const dataToResponse = (data) => ({
  data,
  meta: { totalCount: data.length },
});

export const loadSearchResults = createAsyncThunk(
  "search/loadResults",
  (__next, { dispatch, getState }) => {
    const appliedFilters = selectAppliedFilters(getState());
    if (!appliedFilters.length) {
      return dataToResponse([]);
    }
    const { tkt, pnr, ...params } = {
      ...filtersToParams(appliedFilters),
      ...(__next ? { __next } : {}),
    };
    return (
      tkt
        ? findSaleHistory(tkt).then(dataToResponse)
        : pnr
          ? findSales({ pnr })
          : findSales(params)
    ).catch((err) => {
      dispatch(
        showErrorNotification("Oops, search failed. Please try again later."),
      );
      throw err;
    });
  },
);

export const loadMoreResults = createAsyncThunk(
  "search/loadMoreResults",
  (_, { dispatch, getState }) => {
    const __next = selectNextSearchResultsCursor(getState());
    if (__next) {
      dispatch(loadSearchResults(__next));
    }
  },
);

export const applyFilter = createAsyncThunk(
  "search/applyFilter",
  ({ id, value }, { dispatch, getState }) => {
    const appliedFilterIds = selectAppliedFilterIds(getState());
    if (!appliedFilterIds.includes(id) && STANDALONE_FILTER_IDS.includes(id)) {
      const appliedStandaloneFilterIds = appliedFilterIds.filter((id) =>
        STANDALONE_FILTER_IDS.includes(id),
      );
      appliedStandaloneFilterIds.forEach((id) => dispatch(unsetFilter(id)));
    }
    dispatch(setFilter({ id, value }));
    return dispatch(loadSearchResults())
      .unwrap()
      .catch((err) => {
        dispatch(unsetFilter(id));
        throw err;
      });
  },
);

export const removeFilter = createAsyncThunk(
  "search/removeFilter",
  (id, { dispatch, getState }) => {
    const { value } = selectFilterById(getState(), id);
    dispatch(unsetFilter(id));
    return dispatch(loadSearchResults())
      .unwrap()
      .catch((err) => {
        dispatch(setFilter({ id, value }));
        throw err;
      });
  },
);

export const selectFilterById = (state, id) => state.search.filters.byId[id];

const selectAppliedFilterIds = (state) => state.search.filters.applied;

export const selectAppliedFilters = createSelector(
  (state) => state,
  selectAppliedFilterIds,
  (state, appliedFilterIds) =>
    appliedFilterIds.map((id) => selectFilterById(state, id)),
);

export const selectPossibleFilterIds = createSelector(
  (state) => state.search.filters.byId,
  selectAppliedFilterIds,
  (filtersById, appliedFilterIds) =>
    appliedFilterIds.length
      ? STANDALONE_FILTER_IDS.some((filterId) =>
          appliedFilterIds.includes(filterId),
        )
        ? []
        : Object.keys(filtersById)
      : MANDATORY_FILTER_IDS,
);

export const selectIsSearchInProgress = (state) => state.search.isInProgress;

export const selectNextSearchResultsCursor = (state) => state.search.__next;
